// Lietotnes stāvoklis (zustand). Serveris ir patiesības avots; vērtību vilkšana un slīdņi
// tiek piemēroti uzreiz lokāli un nosūtīti ar aizturi, strukturālās izmaiņas atgriež jaunu {state}.

import type { ReactNode } from 'react';
import { create } from 'zustand';
import {
  buildIndex, computeEnergy, fmtStamp, reanchor, sanitizeTiers, sgn2, sgnR, typicalActivity,
  type AuthConfig, type Card, type CapacityTier, type EnergyNow, type EnergySettings, type Index, type Link, type Me,
  type NodeEnergy, type Settings, type WheelNode, type WheelState
} from '@balansa/shared';
import { ApiError, api } from './lib/api';
import { rememberNext, takeNext } from './lib/next';
import { NEED_BOTH, browserTz, linkProblem } from './lib/helpers';

export type Tab = 'areas' | 'links' | 'energy' | 'analysis' | 'history';

export type Dialog =
  | { kind: 'node'; mode: 'new'; parentId: string | null }
  | { kind: 'node'; mode: 'edit'; id: string }
  | { kind: 'link'; id?: string; from?: string; to?: string }
  | { kind: 'play'; cardId: string }
  | { kind: 'card'; cardId: string | null }
  | { kind: 'import' }
  | { kind: 'mcp' }
  | { kind: 'profile' }
  | null;

export interface ConfirmReq {
  title: string;
  message?: ReactNode;
  ok?: string;
  danger?: boolean;
  resolve: (v: boolean) => void;
}

export interface Toast {
  id: number;
  msg: ReactNode;
  undoId?: string;
  err?: boolean;
}

export interface UI {
  selected: string | null;
  tab: Tab;
  collapsed: Set<string>;
  mode: 'value' | 'link';
  linkSrc: string | null;
  hoverId: string | null;
  hoverLink: string | null;
  linkFilter: boolean;
  deckFilter: string;
  jAll: boolean;
  flash: string[] | null;
  dragging: boolean;
}

export type CardFields = Pick<Card, 'name' | 'typeId' | 'rarity' | 'flavor' | 'energy' | 'kind' | 'effects' | 'rules' | 'stacks'>;

export interface ImportResult {
  nodes: number;
  cards: number;
  images: number;
  missingImages: string[];
}

interface AppStore {
  phase: 'loading' | 'login' | 'ready' | 'error';
  loadError: string | null;
  auth: AuthConfig | null;
  me: Me | null;
  loginError: string | null;
  /** Paroles atjaunošanas saites žetons (no ?reset=…), kamēr lietotājs iestata jaunu paroli. */
  resetToken: string | null;
  wheel: WheelState | null;
  idx: Index | null;
  en: EnergyNow | null;
  ui: UI;
  dialog: Dialog;
  confirm: ConfirmReq | null;
  toast: Toast | null;
  status: { text: string; err: boolean };
  printing: boolean;

  boot(): Promise<void>;
  devLogin(email: string, name: string): Promise<void>;
  /** Ieeja ar e-pastu un paroli; atgriež kļūdas tekstu vai null. */
  loginPassword(email: string, password: string): Promise<string | null>;
  register(name: string, email: string, password: string): Promise<string | null>;
  /** Pēc veiksmīgas ieejas (parole, reģistrācija, paroles atjaunošana): ielādē apli. */
  enter(me: Me): Promise<void>;
  setMe(me: Me): void;
  clearResetToken(): void;
  logout(): Promise<void>;
  /** Ielādē stāvokli no jauna, ja to mainījis cits klients (cita cilne, AI aģents caur MCP). */
  syncFromServer(): Promise<void>;
  deleteAccount(password?: string): Promise<string | null>;
  tick(): void;

  setUi(p: Partial<UI>): void;
  setHover(id: string | null, link: string | null): void;
  pick(id: string): void;
  setTab(t: Tab): void;
  setLinkMode(on: boolean): void;
  toggleCollapse(id: string): void;
  openDialog(d: Dialog): void;
  closeDialog(): void;
  ask(c: Omit<ConfirmReq, 'resolve'>): Promise<boolean>;
  showToast(msg: ReactNode, undoId?: string, err?: boolean): void;
  hideToast(): void;
  setPrinting(on: boolean): void;

  setSettings(p: Partial<Settings>): void;
  setNodeValue(id: string, v: number): void;
  createNode(d: { name: string; parentId: string | null; color?: string | null; note?: string }): Promise<string | null>;
  updateNode(id: string, d: Partial<Pick<WheelNode, 'name' | 'note' | 'parentId' | 'color'>>): Promise<boolean>;
  moveNode(id: string, dir: -1 | 1): Promise<void>;
  deleteNode(id: string): Promise<boolean>;
  setNodeEnergy(id: string, e: NodeEnergy | null, debounce?: boolean): void;
  /** Jomas ietilpības līmeņi (sanitizēti lokāli tāpat kā serverī). */
  setNodeTiers(id: string, tiers: CapacityTier[], debounce?: boolean): void;
  setEnergySettings(p: Partial<Omit<EnergySettings, 'anchor'>>, debounce?: boolean): void;
  saveLink(id: string | null, d: Omit<Link, 'id'>): Promise<boolean>;
  deleteLink(id: string): Promise<void>;
  addCheck(felt: number): Promise<void>;
  addSnapshot(note: string): Promise<void>;
  deleteSnapshot(id: string): Promise<void>;
  resetWheel(): Promise<void>;
  createCard(f: CardFields, image?: File | null): Promise<string | null>;
  updateCard(id: string, f: CardFields, image?: File | null, removeImage?: boolean): Promise<boolean>;
  duplicateCard(id: string, f?: CardFields, image?: File | null): Promise<string | null>;
  deleteCard(id: string): Promise<boolean>;
  playCard(id: string, note: string): Promise<boolean>;
  undoEntry(id: string): Promise<void>;
  importData(file: File, images: File[]): Promise<ImportResult | null>;
}

const initialUi = (): UI => ({
  selected: null,
  tab: 'areas',
  collapsed: new Set(),
  mode: 'value',
  linkSrc: null,
  hoverId: null,
  hoverLink: null,
  linkFilter: true,
  deckFilter: '',
  jAll: false,
  flash: null,
  dragging: false
});

const LOGIN_ERRORS: Record<string, string> = {
  failed: 'Pieteikšanās neizdevās. Mēģini vēlreiz.',
  cancelled: 'Pieteikšanās tika atcelta.',
  unverified: 'Google kontam nav apstiprināta e-pasta adrese.'
};

type WithState = { state?: WheelState };

export const useApp = create<AppStore>()((set, get) => {
  // ---------- atvasinātās vērtības ----------
  const derive = (wheel: WheelState) => {
    const idx = buildIndex(wheel.nodes);
    const now = Date.now();
    const en = computeEnergy(idx, wheel.energy, now, wheel.settings.tz, true, typicalActivity(wheel.journal, now));
    return { wheel, idx, en };
  };
  const applyWheel = (wheel: WheelState) => {
    const d = derive(wheel);
    const ui = get().ui;
    const fix: Partial<UI> = {};
    if (ui.selected && !d.idx.node.has(ui.selected)) fix.selected = null;
    if (ui.linkSrc && !d.idx.node.has(ui.linkSrc)) fix.linkSrc = null;
    if (ui.hoverId && !d.idx.node.has(ui.hoverId)) fix.hoverId = null;
    if (ui.hoverLink && !wheel.links.some((l) => l.id === ui.hoverLink)) fix.hoverLink = null;
    if (ui.deckFilter && !d.idx.node.has(ui.deckFilter)) fix.deckFilter = '';
    set({ ...d, ui: Object.keys(fix).length ? { ...ui, ...fix } : ui });
  };
  /** Lokāla (optimistiska) izmaiņa. */
  let localRev = 0;
  const local = (fn: (w: WheelState) => WheelState) => {
    const w = get().wheel;
    if (!w) return;
    localRev++;
    applyWheel(fn(w));
  };
  /**
   * Lokāla izmaiņa, kas maina enerģijas modeli (vērtības, lomas, līmeņi, ritms): vispirms nofiksē pašreizējo līmeni
   * kā enkuru — tāpat kā serveris (ADR 0012), lai izmaiņa nedarbotos retroaktīvi.
   */
  const localModel = (fn: (w: WheelState) => WheelState) =>
    local((w) => fn({ ...w, energy: reanchor(buildIndex(w.nodes), w.energy, Date.now(), w.settings.tz) }));

  // ---------- sinhronizācija ar serveri ----------
  let inflight = 0;
  let needsRefresh = false;
  const debouncers = new Map<string, { timer: ReturnType<typeof setTimeout>; run: () => Promise<unknown> }>();

  const setStatus = (text: string, err = false) => set({ status: { text, err } });
  const settle = () => {
    if (inflight || debouncers.size) return;
    if (needsRefresh) {
      needsRefresh = false;
      void send(() => api.get<WithState>('/wheel'), true);
      return;
    }
    if (!get().status.err) setStatus('Saglabāts · ' + fmtStamp(Date.now()));
  };

  const handleError = (e: unknown) => {
    if (e instanceof ApiError && e.status === 401) {
      set({ phase: 'login', me: null, wheel: null, idx: null, en: null, loginError: 'Sesija ir beigusies — piesakies vēlreiz.' });
      return;
    }
    const msg = e instanceof Error ? e.message : 'Neizdevās saglabāt.';
    setStatus(msg, true);
    get().showToast(msg, undefined, true);
    needsRefresh = true; // atjaunojam no servera, lai lokālais stāvoklis nebūtu nepatiess
  };

  /** Nosūta pieprasījumu; ja atbildē ir {state} un pa to laiku nav bijušas lokālas izmaiņas — piemēro. */
  async function send<T>(p: () => Promise<T & WithState>, quiet = false): Promise<(T & WithState) | null> {
    const rev = localRev;
    inflight++;
    if (!quiet) set({ status: { text: 'Saglabā…', err: false } });
    try {
      const r = await p();
      if (r && r.state) {
        // Piemēro tikai tad, ja pa to laiku nav lokālu izmaiņu un neviens cits pieprasījums vēl nav ceļā —
        // citādi atbilde varētu būt vecāka par jau nosūtītu izmaiņu; tad pēc nomierināšanās nolasa visu no jauna.
        if (rev === localRev && !debouncers.size && inflight === 1) applyWheel(r.state);
        else needsRefresh = true;
      }
      if (!quiet && get().status.err) setStatus('');
      return r;
    } catch (e) {
      handleError(e);
      return null;
    } finally {
      inflight--;
      settle();
    }
  }

  const fire = async (key: string) => {
    const d = debouncers.get(key);
    if (!d) return;
    clearTimeout(d.timer);
    debouncers.delete(key);
    await d.run();
  };
  const debounce = (key: string, ms: number, run: () => Promise<unknown>) => {
    const d = debouncers.get(key);
    if (d) clearTimeout(d.timer);
    debouncers.set(key, { timer: setTimeout(() => void fire(key), ms), run });
    set({ status: { text: 'Saglabā…', err: false } });
  };
  const flushPending = () => Promise.all([...debouncers.keys()].map(fire));

  /** Strukturāla darbība: vispirms nosūta aizturētās izmaiņas, tad izpilda pieprasījumu. */
  async function call<T>(p: () => Promise<T & WithState>) {
    await flushPending();
    return send(p);
  }

  // Aizturēto vērtību rinda (vilkšana, slīdņi) un daļējie iestatījumu labojumi.
  let pendingValues: Record<string, number> = {};
  let pendingSettings: Partial<Settings> = {};
  let pendingEnergy: Partial<Omit<EnergySettings, 'anchor'>> = {};
  const flushValues = () => {
    const values = pendingValues;
    pendingValues = {};
    if (!Object.keys(values).length) return Promise.resolve(null);
    return send(() => api.patch<{ ok: true }>('/nodes/values', { values }));
  };

  window.addEventListener('beforeunload', (e) => {
    if (debouncers.size || inflight) {
      void flushPending();
      e.preventDefault();
    }
  });

  const loadWheel = async () => {
    const r = await api.get<{ state: WheelState }>('/wheel');
    applyWheel(r.state);
    set({ phase: 'ready', status: { text: 'Saglabāts · ' + fmtStamp(r.state.updatedAt), err: false } });
    // Diennakts ritms tiek rēķināts lietotāja laika joslā — uzstādām pārlūka joslu.
    // Pārlūka laika joslu uzstāda tikai tad, ja tā šajā ierīcē mainījusies (ceļojums / jauna ierīce),
    // lai nepārrakstītu apzināti iestatītu joslu (piem. caur MCP).
    const tz = browserTz();
    let last: string | null = null;
    try {
      last = localStorage.getItem('balansa.tz');
    } catch {
      /* krātuve nav pieejama */
    }
    if (tz !== last) {
      try {
        localStorage.setItem('balansa.tz', tz);
      } catch {
        /* krātuve nav pieejama */
      }
      if (r.state.settings.tz !== tz) get().setSettings({ tz });
    }
  };

  return {
    phase: 'loading',
    loadError: null,
    auth: null,
    me: null,
    loginError: null,
    resetToken: null,
    wheel: null,
    idx: null,
    en: null,
    ui: initialUi(),
    dialog: null,
    confirm: null,
    toast: null,
    status: { text: '', err: false },
    printing: false,

    // ---------- autentifikācija ----------
    async boot() {
      const qs = new URLSearchParams(location.search);
      const lf = qs.get('login');
      if (lf) {
        set({ loginError: LOGIN_ERRORS[lf] || LOGIN_ERRORS.failed });
        history.replaceState(null, '', location.pathname);
      }
      const reset = qs.get('reset');
      const verify = qs.get('verify');
      const next = qs.get('next');
      if (next !== null) rememberNext(next);
      if (reset || verify || next !== null) history.replaceState(null, '', location.pathname);
      try {
        const auth = await api.get<AuthConfig>('/auth/config');
        set({ auth });
        if (verify) {
          try {
            await api.post('/auth/verify', { token: verify });
            get().showToast('E-pasta adrese apstiprināta ✓');
          } catch (e) {
            get().showToast(e instanceof Error ? e.message : 'Apstiprināšana neizdevās.', undefined, true);
          }
        }
        if (reset) {
          // Jaunas paroles iestatīšana — neatkarīgi no tā, vai šajā pārlūkā kāds ir pieteicies.
          set({ resetToken: reset, phase: 'login' });
          return;
        }
        const me = await api.get<Me>('/auth/me');
        const go = takeNext();
        if (go) return void location.assign(go); // jau pieteicies — atpakaļ uz OAuth piekrišanu
        set({ me });
        await loadWheel();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) set({ phase: 'login' });
        else set({ phase: 'error', loadError: e instanceof Error ? e.message : String(e) });
      }
    },
    async devLogin(email, name) {
      try {
        const me = await api.post<Me>('/auth/dev-login', name ? { email, name } : { email });
        await get().enter(me);
      } catch (e) {
        set({ loginError: e instanceof Error ? e.message : 'Neizdevās pieteikties.' });
      }
    },
    async loginPassword(email, password) {
      try {
        const me = await api.post<Me>('/auth/login', { email, password });
        await get().enter(me);
        return null;
      } catch (e) {
        return e instanceof Error ? e.message : 'Neizdevās pieteikties.';
      }
    },
    async register(name, email, password) {
      try {
        const me = await api.post<Me>('/auth/register', { name: name || undefined, email, password });
        await get().enter(me);
        return null;
      } catch (e) {
        return e instanceof Error ? e.message : 'Neizdevās izveidot kontu.';
      }
    },
    async enter(me) {
      const go = takeNext();
      if (go) return void location.assign(go); // pēc pieteikšanās — atpakaļ uz OAuth piekrišanu (ChatGPT u.c.)
      set({ me, loginError: null, resetToken: null, ui: initialUi() });
      try {
        await loadWheel();
      } catch (e) {
        set({ phase: 'error', loadError: e instanceof Error ? e.message : String(e) });
      }
    },
    setMe(me) {
      set({ me });
    },
    clearResetToken() {
      set({ resetToken: null });
    },
    async logout() {
      await flushPending();
      try {
        await api.post('/auth/logout');
      } catch {
        /* sesija jau varētu būt beigusies */
      }
      set({ phase: 'login', me: null, wheel: null, idx: null, en: null, dialog: null, ui: initialUi(), loginError: null });
    },
    async deleteAccount(password) {
      try {
        await api.del('/account', password ? { password } : undefined);
        set({ phase: 'login', me: null, wheel: null, idx: null, en: null, dialog: null, ui: initialUi(), loginError: 'Konts un visi dati ir dzēsti.' });
        return null;
      } catch (e) {
        return e instanceof Error ? e.message : 'Neizdevās dzēst kontu.';
      }
    },
    async syncFromServer() {
      const w = get().wheel;
      if (!w || inflight || debouncers.size || get().ui.dragging) return;
      try {
        const v = await api.get<{ updatedAt: number }>('/wheel/version');
        if (v.updatedAt !== get().wheel?.updatedAt && !inflight && !debouncers.size) await send(() => api.get<WithState>('/wheel'), true);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) handleError(e);
        /* citas (tīkla) kļūdas — nākamajā reizē */
      }
    },
    tick() {
      const w = get().wheel;
      if (!w || get().ui.dragging) return;
      set(derive(w));
    },

    // ---------- UI ----------
    setUi(p) {
      set({ ui: { ...get().ui, ...p } });
    },
    setHover(id, link) {
      const ui = get().ui;
      if (ui.hoverId === id && ui.hoverLink === link) return;
      set({ ui: { ...ui, hoverId: id, hoverLink: link } });
    },
    pick(id) {
      const { idx, ui } = get();
      if (!idx || !idx.node.has(id)) return;
      if (ui.mode === 'link') {
        if (!ui.linkSrc) get().setUi({ linkSrc: id });
        else if (ui.linkSrc === id) get().setUi({ linkSrc: null });
        else {
          const from = ui.linkSrc;
          const pr = linkProblem(idx, from, id);
          if (pr && pr !== NEED_BOTH) get().setUi({ linkSrc: id });
          else {
            get().setUi({ linkSrc: null });
            get().openDialog({ kind: 'link', from, to: id });
          }
        }
        return;
      }
      const selected = ui.selected === id ? null : id;
      const collapsed = new Set(ui.collapsed);
      if (selected) idx.anc.get(selected)!.slice(1).forEach((a) => collapsed.delete(a));
      set({ ui: { ...ui, selected, collapsed } });
    },
    setTab(tab) {
      get().setUi({ tab });
      if (tab === 'energy') get().tick();
    },
    setLinkMode(on) {
      get().setUi({ mode: on ? 'link' : 'value', linkSrc: null });
    },
    toggleCollapse(id) {
      const collapsed = new Set(get().ui.collapsed);
      if (collapsed.has(id)) collapsed.delete(id);
      else collapsed.add(id);
      get().setUi({ collapsed });
    },
    openDialog(dialog) {
      set({ dialog });
    },
    closeDialog() {
      set({ dialog: null });
    },
    ask(c) {
      return new Promise<boolean>((resolve) => {
        set({
          confirm: {
            ...c,
            resolve: (v) => {
              set({ confirm: null });
              resolve(v);
            }
          }
        });
      });
    },
    showToast(msg, undoId, err) {
      set({ toast: { id: Date.now() + Math.random(), msg, undoId, err } });
    },
    hideToast() {
      set({ toast: null });
    },
    setPrinting(printing) {
      set({ printing });
    },

    // ---------- apļa iestatījumi un vērtības ----------
    setSettings(p) {
      local((w) => ({ ...w, settings: { ...w.settings, ...p } }));
      // Sūta tikai mainītos laukus — lai nepārrakstītu cita klienta (piem. AI aģenta) izmaiņas citos laukos.
      pendingSettings = { ...pendingSettings, ...p };
      debounce('settings', 300, () => {
        const body = pendingSettings;
        pendingSettings = {};
        return send(() => api.patch<{ ok: true }>('/wheel/settings', body));
      });
    },
    setNodeValue(id, v) {
      const w = get().wheel;
      const n = w?.nodes.find((x) => x.id === id);
      if (!n || n.value === v) return;
      localModel((w2) => ({ ...w2, nodes: w2.nodes.map((x) => (x.id === id ? { ...x, value: v } : x)) }));
      pendingValues[id] = v;
      debounce('values', 400, flushValues);
    },

    // ---------- jomas ----------
    async createNode(d) {
      const r = await call(() => api.post<{ id: string }>('/nodes', d));
      if (r && d.parentId) {
        const collapsed = new Set(get().ui.collapsed);
        collapsed.delete(d.parentId);
        get().setUi({ collapsed });
      }
      return r ? r.id : null;
    },
    async updateNode(id, d) {
      return !!(await call(() => api.patch(`/nodes/${id}`, d)));
    },
    async moveNode(id, dir) {
      await call(() => api.post(`/nodes/${id}/move`, { dir }));
    },
    async deleteNode(id) {
      return !!(await call(() => api.del(`/nodes/${id}`)));
    },
    setNodeEnergy(id, e, deb = false) {
      localModel((w) => ({ ...w, nodes: w.nodes.map((x) => (x.id === id ? { ...x, energy: e } : x)) }));
      const run = () => {
        const n = get().wheel?.nodes.find((x) => x.id === id);
        if (!n) return Promise.resolve(null);
        return send(() => api.patch(`/nodes/${id}`, { energy: n.energy }));
      };
      if (deb) debounce('energy:' + id, 400, run);
      else void flushPending().then(run);
    },
    setNodeTiers(id, tiers, deb = false) {
      const clean = sanitizeTiers(tiers);
      localModel((w) => ({ ...w, nodes: w.nodes.map((x) => (x.id === id ? { ...x, tiers: clean } : x)) }));
      const run = () => {
        const n = get().wheel?.nodes.find((x) => x.id === id);
        if (!n) return Promise.resolve(null);
        return send(() => api.patch(`/nodes/${id}`, { tiers: n.tiers }));
      };
      if (deb) debounce('tiers:' + id, 500, run);
      else void flushPending().then(run);
    },
    setEnergySettings(p, deb = false) {
      localModel((w) => ({ ...w, energy: { ...w.energy, ...p } }));
      pendingEnergy = { ...pendingEnergy, ...p };
      const run = () => {
        const body = pendingEnergy;
        pendingEnergy = {};
        if (!Object.keys(body).length) return Promise.resolve(null);
        return send(() => api.patch('/wheel/energy', body));
      };
      if (deb) debounce('wheel-energy', 400, run);
      else void flushPending().then(run);
    },

    // ---------- saites ----------
    async saveLink(id, d) {
      const body = { fromId: d.fromId, toId: d.toId, type: d.type, label: d.label, strength: d.strength };
      const r = id ? await call(() => api.patch(`/links/${id}`, body)) : await call(() => api.post('/links', body));
      return !!r;
    },
    async deleteLink(id) {
      get().setHover(null, null);
      await call(() => api.del(`/links/${id}`));
    },

    // ---------- enerģija un vēsture ----------
    async addCheck(felt) {
      await call(() => api.post('/energy/checks', { felt }));
    },
    async addSnapshot(note) {
      await call(() => api.post('/snapshots', { note }));
    },
    async deleteSnapshot(id) {
      await call(() => api.del(`/snapshots/${id}`));
    },
    async resetWheel() {
      const r = await call(() => api.post('/wheel/reset'));
      if (r) set({ ui: { ...initialUi(), tab: get().ui.tab } });
    },

    // ---------- kārtis ----------
    async createCard(f, image) {
      const r = await call(() => api.post<{ id: string }>('/cards', { ...f }));
      if (!r) return null;
      if (image) await uploadImage(r.id, image);
      return r.id;
    },
    async updateCard(id, f, image, removeImage) {
      const r = await call(() => api.patch(`/cards/${id}`, { ...f }));
      if (!r) return false;
      if (image) await uploadImage(id, image);
      else if (removeImage) await call(() => api.del(`/cards/${id}/image`));
      return true;
    },
    async duplicateCard(id, f, image) {
      const r = await call(() => api.post<{ id: string }>(`/cards/${id}/duplicate`));
      if (!r) return null;
      if (f) await call(() => api.patch(`/cards/${r.id}`, { ...f, name: (f.name + ' (kopija)').slice(0, 40) }));
      if (image) await uploadImage(r.id, image);
      return r.id;
    },
    async deleteCard(id) {
      return !!(await call(() => api.del(`/cards/${id}`)));
    },
    async playCard(id, note) {
      const card = get().wheel?.cards.find((c) => c.id === id);
      const r = await call(() =>
        api.post<{ entryId: string; outcome: { energyDelta: number; effects: { target: string; name: string; delta: number }[]; stacks?: string[] } }>(
          `/cards/${id}/play`,
          { note }
        )
      );
      if (!r) return false;
      const targets = r.outcome.effects.map((e) => e.target);
      get().setUi({ flash: targets });
      setTimeout(() => get().setUi({ flash: null }), 1700);
      get().showToast(
        <ToastPlayed name={card?.name ?? 'Kārts'} delta={r.outcome.energyDelta} effects={r.outcome.effects} stacks={r.outcome.stacks ?? []} />,
        r.entryId
      );
      return true;
    },
    async undoEntry(id) {
      const j = get().wheel?.journal.find((x) => x.id === id);
      const r = await call(() => api.post(`/journal/${id}/undo`));
      if (r) get().showToast(<>Atsaukts: <b>{j?.name ?? 'ieraksts'}</b></>);
    },
    async importData(file, images) {
      const fd = new FormData();
      fd.append('file', file);
      images.forEach((im) => fd.append('images', im, im.name));
      const r = await call(() => api.post<{ imported: ImportResult }>('/import', fd));
      if (!r) return null;
      set({ ui: { ...initialUi(), tab: get().ui.tab } });
      return r.imported;
    }
  };

  async function uploadImage(cardId: string, file: File) {
    const fd = new FormData();
    fd.append('image', file, file.name);
    await call(() => api.post(`/cards/${cardId}/image`, fd));
  }
});

// Izspēles paziņojuma saturs.
function ToastPlayed(p: { name: string; delta: number; effects: { name: string; delta: number }[]; stacks: string[] }) {
  return (
    <>
      <b>{p.name}</b> izspēlēta · ⚡ {sgnR(p.delta)} v.
      {p.effects.length ? ' · ' + p.effects.slice(0, 3).map((e) => e.name + ' ' + sgn2(e.delta)).join(', ') : ''}
      {p.stacks.length ? (
        <span data-testid="toast-stack">
          {' '}
          · ⟳ krāšanās bonuss: {p.stacks.map((s) => s.split(/[(:]/)[0].trim()).join(', ')}
        </span>
      ) : null}
    </>
  );
}

/** Ērtība komponentiem, kas tiek renderēti tikai stāvoklī “ready”. */
export function useWheel() {
  const wheel = useApp((s) => s.wheel)!;
  const idx = useApp((s) => s.idx)!;
  const en = useApp((s) => s.en)!;
  return { wheel, idx, en };
}
